import { Switch } from '@radix-ui/themes'
import { CreatePolicyInput, PolicyConfig, PolicyActionsInput } from '../../types/SettingsType.ts'
import { IconCheck, IconUsersGroup, IconX } from '@tabler/icons-react'
import { SelectedGroup } from '../../types/GroupTypes.ts'

interface IPolicySummaryProps {
  policyDetails: CreatePolicyInput | undefined
  policyRules: Partial<PolicyConfig>
  policyActionDetails: PolicyActionsInput | undefined
  activateOnCreate: boolean
  setActivateOnCreate: (active: boolean) => void
  screen?: string
  selectedGroups: SelectedGroup[]
}

function PolicySummary(props: IPolicySummaryProps) {
  const {
    policyDetails,
    policyRules,
    policyActionDetails,
    activateOnCreate,
    setActivateOnCreate,
    screen,
    selectedGroups,
  } = props

  const mapTimeToText = (time: string) => {
    switch (time) {
      case 'ONE_DAY':
        return '1 day'
      case 'THREE_DAYS':
        return '3 days'
      case 'SEVEN_DAYS':
        return '7 days'
      case 'TWO_WEEKS':
        return '2 weeks'
      case 'THREE_WEEKS':
        return '3 weeks'
      case 'ONE_MONTH':
        return '1 month'
      default:
        return 'Never'
    }
  }

  const mapSeverityToText = (severity: string) => {
    switch (severity) {
      case 'CRITICAL_ONLY':
        return 'Critical Only'
      case 'HIGH_AND_CRITICAL':
        return 'High and Critical'
      case 'HIGH_AND_MEDIUM':
        return 'High and Medium'
      case 'HIGH':
        return 'High Risk Only'
      case 'NONE':
        return 'None'
      default:
        return 'None'
    }
  }

  return (
    <>
      <div className="flex flex-col gap-4 mb-14">
        <div className="w-[800px] p-4 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder inline-flex flex-col justify-end items-start overflow-hidden mx-auto">
          <div className="w-full flex justify-between">
            <div className="justify-center text-mainText font-semibold">Summary</div>
          </div>
          <hr className="w-full h-px bg-mainBorder my-4" />
          <p className="text-xs text-textTertiary mb-2">Name</p>
          <p className="text-sm text-mainText mb-6">{policyDetails?.name}</p>
          <p className="text-xs text-textTertiary mb-2">Description</p>
          <div className="min-w-0">
            <p className="text-sm text-mainText mb-2 whitespace-pre-wrap break-all">
              {policyDetails?.description ? policyDetails?.description : '--'}
            </p>
          </div>

          <hr className="w-full h-px bg-mainBorder my-4" />
          <p className="text-xs text-textTertiary mb-2">Rules</p>

          <div className="flex flex-col gap-2 w-full">
            {!policyRules || Object.keys(policyRules).length === 0 ? (
              <p className="text-sm text-textTertiary">No rules configured</p>
            ) : (
              <>
                {policyRules.iosMinimumVersion && (
                  <div className="flex">
                    <IconCheck size={16} className="mr-2 mt-0.5" />
                    <p className="text-sm">
                      Requires iOS devices to run version {policyRules.iosMinimumVersion} or newer
                    </p>
                  </div>
                )}

                {policyRules.iosVersionOffset && (
                  <div className="flex">
                    <IconCheck size={16} className="mr-2 mt-0.5" />
                    <p className="text-sm">
                      Requires iOS devices to be within {policyRules.iosVersionOffset} versions of the latest release
                      <span className="text-textTertiary">
                        {' ('}
                        {policyRules.iosMinimumVersionGracePeriod}{' '}
                        {policyRules.iosMinimumVersionGracePeriodUnit?.toLowerCase()}
                        {' grace period)'}
                      </span>
                    </p>
                  </div>
                )}

                {policyRules.androidMinimumVersion && (
                  <div className="flex">
                    <IconCheck size={16} className="mr-2 mt-0.5" />
                    <p className="text-sm">
                      Requires Android devices to run version {policyRules.androidMinimumVersion} or newer
                    </p>
                  </div>
                )}

                {policyRules.androidLastPatchVersionGracePeriod && (
                  <div className="flex">
                    <IconCheck size={16} className="mr-2 mt-0.5" />
                    <p className="text-sm">
                      Requires Android devices to update their patch level within{' '}
                      {policyRules.androidLastPatchVersionGracePeriod}{' '}
                      <span className="capitalize">
                        {policyRules.androidLastPatchVersionGracePeriodUnit?.toLowerCase()}
                      </span>
                    </p>
                  </div>
                )}

                {policyRules.biometricsEnabled && (
                  <div className="flex">
                    <IconCheck size={16} className="mr-2 mt-0.5" />
                    <p className="text-sm">Requires devices to enable Biometrics</p>
                  </div>
                )}

                {policyRules.smishingEnabled && (
                  <div className="flex">
                    <IconCheck size={16} className="mr-2 mt-0.5" />
                    <p className="text-sm">Requires devices to enable Smishing Protection</p>
                  </div>
                )}

                {policyRules.screenLockEnabled && (
                  <div className="flex">
                    <IconCheck size={16} className="mr-2 mt-0.5" />
                    <p className="text-sm">Requires devices to enable Screen Lock</p>
                  </div>
                )}

                {policyRules.deviceIntegrityPeriod && policyRules.deviceIntegrityPeriod !== 'NEVER' && (
                  <div className="flex">
                    <IconCheck size={16} className="mr-2 mt-0.5" />
                    <p className="text-sm">
                      Requires devices to check-in every {mapTimeToText(policyRules.deviceIntegrityPeriod!)}
                    </p>
                  </div>
                )}

                {policyRules.alertSeverity && policyRules.alertSeverity !== 'NONE' && (
                  <div className="flex">
                    <IconCheck size={16} className="mr-2 mt-0.5" />
                    <p className="text-sm">
                      Require devices to have no Open Alerts of Severity {mapSeverityToText(policyRules.alertSeverity)}
                    </p>
                  </div>
                )}

                {policyRules.travelEnabled && (
                  <div className="flex">
                    <IconCheck size={16} className="mr-2 mt-0.5" />
                    <p className="text-sm">Requires iOS devices to share location</p>
                  </div>
                )}
              </>
            )}
          </div>

          <hr className="w-full h-px bg-mainBorder my-4" />
          <p className="text-xs text-textTertiary mb-2">Groups Impacted</p>
          {selectedGroups?.map((group) => (
            <div className="flex">
              <IconUsersGroup size={16} className="mr-2 mt-0.5" />
              <p className="text-sm">
                {group.name} ({group.memberCount} {group.memberCount === 1 ? 'member' : 'members'})
              </p>
            </div>
          ))}
          {selectedGroups.length === 0 && (
            <div className="flex">
              <IconX size={16} className="mr-2 mt-0.5" />
              <p className="text-sm">All members</p>
            </div>
          )}

          <hr className="w-full h-px bg-mainBorder my-4" />
          <p className="text-xs text-textTertiary mb-2">Integration</p>
          <p className="justify-center text-mainText font-medium">{policyActionDetails?.integrationName}</p>
          {policyActionDetails?.integrationName === 'Microsoft Intune Mobile Threat Defence' && (
            <div className="flex mt-2 gap-6">
              <p className="text-sm text-textTertiary">
                Threat Level:{' '}
                <span className="text-mainText capitalize">{policyActionDetails?.threatLevel?.toLowerCase()}</span>
              </p>
            </div>
          )}
          <div className="w-full p-4 bg-[#FAFAFA] rounded-lg outline outline-1 outline-mainBorder inline-flex flex-col justify-end items-start overflow-hidden mx-auto mt-4">
            <div className="w-full flex justify-between">
              <div className="block">
                <p className="justify-center text-textTertiary text-xs">
                  {screen === 'EDIT' ? 'Activate policy' : 'Activate policy on creation'}
                </p>
                <div className="text-mainText text-sm">
                  When active, this policy is enforced across all selected groups and their members
                </div>
              </div>
              <div className="mt-[11px]">
                <Switch
                  size="2"
                  className="light"
                  checked={activateOnCreate}
                  onCheckedChange={(checked) => setActivateOnCreate(checked)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PolicySummary
